import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/scss';
import EcoImg from 'eco-design/base/eco-img';
import EcoSpace from 'eco-design/base/eco-space';
import gtm from 'eco-design-reliance/gtm';
import { useGlobal } from '@/utils/useGlobal';
import useCardsShowHook from '../useHooks/useCardsShowHook';
import styles from './index.module.scss';

type SwiperInstance = {
    realIndex: number;
    slideToLoop: (index: number) => void;
};

const B = () => {
    const { useLang } = useGlobal();
    const { t } = useLang('outdoorrecreation');
    const [selectIndex, setSelectIndex] = useState(0);
    const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, transform: 'translateX(0px)' });
    const solarEnergyRef = useRef<HTMLDivElement>(null);
    const swiperRef = useRef<SwiperInstance | null>(null);
    const tabsBoxRef = useRef<HTMLDivElement>(null);
    const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

    const tabs = [
        {
            title: t('secB_tab1'),
            label: t('secB_tab1'),
            img: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1785853654126-ja4i6d-compressed-Camping.webp',
                h5: 'https://websiteoss.ecoflow.com/upload/image/1768186914065/Frame%202094654830.jpg',
            },
        },
        {
            title: t('secB_tab2'),
            label: t('secB_tab2'),
            img: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1785853658704-xe5otk-compressed-Tailgating.webp',
                h5: 'https://websiteoss.ecoflow.com/upload/image/1768186913844/Frame%202094654831.jpg',
            },
        },
        {
            title: t('secB_tab3'),
            label: t('secB_tab3'),
            img: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1785853664395-lp7a1u-compressed-Fishing.webp',
                h5: 'https://websiteoss.ecoflow.com/upload/image/1768186913668/Frame%202094654832.jpg',
            },
        },
        {
            title: t('secB_tab4'),
            label: t('secB_tab4'),
            img: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1785853668415-gqjscm-compressed-Boating.webp',
                h5: 'https://websiteoss.ecoflow.com/upload/image/1768186913668/Frame%202094654832.jpg',
            },
        },
        {
            title: t('secB_tab5'),
            label: t('secB_tab5'),
            img: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1785853677259-p57vkl-compressed-RV%26Caravan.webp',
                h5: 'https://websiteoss.ecoflow.com/upload/image/1768186913668/Frame%202094654832.jpg',
            },
        },
    ];

    const updateIndicator = (index: number) => {
        const el = tabRefs.current[index];
        if (!el) return;
        setIndicatorStyle({
            width: el.offsetWidth,
            transform: `translateX(${el.offsetLeft}px)`,
        });
    };

    const scrollActiveTabIntoView = (index: number, behavior: ScrollBehavior = 'smooth') => {
        const tabsBox = tabsBoxRef.current;
        const tab = tabRefs.current[index];
        const tabs = tab?.parentElement;
        if (!tabsBox || !tab || !tabs) return;

        const tabCenter = tabs.offsetLeft + tab.offsetLeft + tab.offsetWidth / 2;
        tabsBox.scrollTo({
            left: tabCenter - tabsBox.clientWidth / 2,
            behavior,
        });
    };

    useLayoutEffect(() => {
        updateIndicator(selectIndex);
        scrollActiveTabIntoView(selectIndex);
    }, [selectIndex, tabs.length]);

    useEffect(() => {
        const onResize = () => {
            updateIndicator(selectIndex);
            scrollActiveTabIntoView(selectIndex, 'auto');
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [selectIndex]);

    const handleSlideChange = (swiper: { realIndex: number }) => {
        setSelectIndex(swiper.realIndex);
    };

    const handleTabClick = (idx: number, label: string) => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'tab_click',
            list_name: t('secB_title'),
            button_name: label,
            __btn_id1: `B-tabs-box .B-tabs-item-${idx + 1}`,
            __btn_id2: label,
            __btn_type: 'text',
        });
        if (idx === selectIndex) {
            scrollActiveTabIntoView(idx);
            return;
        }
        setSelectIndex(idx);
        swiperRef.current?.slideToLoop(idx);
    };

    useCardsShowHook('.solar-energy-container .CardsShow', '.solar-energy-container');

    return (
        <section
            className={`outdoor-recreation-B GtmModuleData-section`}
            id="outdoor-recreation-B"
            data-section-id="outdoor-recreation-B"
            data-section-title={t('secB_title')}
        >
            <EcoSpace />
            <div className={styles['solar-energy-solutions']} ref={solarEnergyRef} id="solarEnergy">
                <div className="solar-energy-container">
                    <div className="text-info CardsShow">
                        <h2 className="title">{t('secB_title')}</h2>
                        <p className="desc">{t(`secB_tab${selectIndex + 1}_desc`)}</p>
                    </div>
                    <Swiper
                        className="CardsShow"
                        slidesPerView="auto"
                        centeredSlides
                        spaceBetween={12}
                        navigation={{
                            nextEl: '.eco-swiper-button-next',
                            prevEl: '.eco-swiper-button-prev',
                        }}
                        modules={[Navigation]}
                        loop
                        onSlideChange={handleSlideChange}
                        onSwiper={(swiperIns) => {
                            swiperRef.current = swiperIns;
                        }}
                    >
                        {tabs.map((item, index) => (
                            <SwiperSlide key={index} className="swiper-slide solar-energy-img-container ">
                                <EcoImg className="item-img" pc={item.img.pc} pad={item.img.h5} />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
                <div className="B-tabs-box" ref={tabsBoxRef}>
                    <div className="B-tabs">
                        <div className="B-tabs-indicator" style={indicatorStyle} />

                        {tabs.map((i, idx) => (
                            <div
                                className={`B-tabs-item B-tabs-item-${idx + 1} ${idx === selectIndex ? 'active' : ''}`}
                                onClick={() => handleTabClick(idx, i.label)}
                                key={idx}
                                ref={(el) => {
                                    tabRefs.current[idx] = el;
                                }}
                            >
                                {i.label}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default B;
